import { useState, useEffect } from 'react';
import { getAdminTeams, createAdminTeam, updateAdminTeam, deleteAdminTeam, type BackendTeam } from '@/services/admin/teams.admin.api';
import ImageUploader from '@/components/base/ImageUploader';
import RichTextEditor from '@/components/base/RichTextEditor';
import { useRBAC } from '@/hooks/useRBAC';
import NoPermission from '@/components/base/NoPermission';

type TeamRole = 'executive' | 'advisory';

const ROLE_OPTIONS: { value: TeamRole; label: string }[] = [
  { value: 'executive', label: '执行团队' },
  { value: 'advisory', label: '顾问委员会' },
];

const BLANK: Partial<BackendTeam> = {
  name: '',
  nameEn: '',
  title: '',
  titleEn: '',
  institution: '',
  institutionEn: '',
  photo: '',
  bio: '',
  bioEn: '',
  role: 'executive',
  linkedin: '',
};

export default function AdminTeamsPage() {
  const { can } = useRBAC();
  const [teams, setTeams] = useState<BackendTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<BackendTeam | null>(null);
  const [form, setForm] = useState<Partial<BackendTeam>>(BLANK);
  const [filterRole, setFilterRole] = useState<TeamRole | 'all'>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const canWrite = can('teams:write');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const data = await getAdminTeams();
      setTeams(data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = filterRole === 'all' ? teams : teams.filter((t) => t.role === filterRole);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setShowModal(true);
  };

  const openEdit = (team: BackendTeam) => {
    setEditing(team);
    setForm({ ...team });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updateAdminTeam(editing.id, form);
      } else {
        await createAdminTeam(form);
      }
      setShowModal(false);
      fetchTeams();
    } catch (error) {
      console.error('Failed to save team:', error);
      alert(editing ? '更新失败' : '创建失败');
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteAdminTeam(deleteId);
      setShowDeleteConfirm(false);
      setDeleteId(null);
      fetchTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      alert('删除失败');
    }
  };

  if (!can('teams:view')) {
    return <NoPermission />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">团队成员管理</h1>
          <p className="mt-1 text-sm text-gray-500">管理执行团队和顾问委员会成员</p>
        </div>
        {canWrite && (
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            添加成员
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="mb-4 flex gap-2">
        {(['all', 'executive', 'advisory'] as const).map((role) => (
          <button
            key={role}
            onClick={() => setFilterRole(role)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterRole === role
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {role === 'all' ? '全部' : role === 'executive' ? '执行团队' : '顾问委员会'}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">加载中...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">暂无数据</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">姓名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">职位</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">机构</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">角色</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">照片</th>
                {canWrite && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{team.name}</div>
                    <div className="text-xs text-gray-500">{team.nameEn}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{team.title}</div>
                    <div className="text-xs text-gray-500">{team.titleEn}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{team.institution}</div>
                    <div className="text-xs text-gray-500">{team.institutionEn}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        team.role === 'executive'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {team.role === 'executive' ? '执行团队' : '顾问委员会'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {team.photo ? (
                      <img
                        src={team.photo}
                        alt={team.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                        无
                      </div>
                    )}
                  </td>
                  {canWrite && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEdit(team)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => confirmDelete(team.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        删除
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editing ? '编辑成员' : '添加成员'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">角色 *</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as TeamRole })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">姓名（中文）*</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="张三"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">姓名（英文）</label>
                  <input
                    type="text"
                    value={form.nameEn}
                    onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Zhang San"
                  />
                </div>
              </div>

              {/* Title */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">职位（中文）</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="CEO"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">职位（英文）</label>
                  <input
                    type="text"
                    value={form.titleEn}
                    onChange={(e) => setForm({ ...form, titleEn: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Chief Executive Officer"
                  />
                </div>
              </div>

              {/* Institution */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">机构（中文）</label>
                  <input
                    type="text"
                    value={form.institution}
                    onChange={(e) => setForm({ ...form, institution: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="某公司"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">机构（英文）</label>
                  <input
                    type="text"
                    value={form.institutionEn}
                    onChange={(e) => setForm({ ...form, institutionEn: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="ABC Corp"
                  />
                </div>
              </div>

              {/* Photo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">照片</label>
                <ImageUploader
                  value={form.photo}
                  onChange={(url) => setForm({ ...form, photo: url })}
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">简介（中文）</label>
                <RichTextEditor
                  content={form.bio || ''}
                  onChange={(html) => setForm({ ...form, bio: html })}
                  placeholder="个人简介..."
                  minHeight="150px"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">简介（英文）</label>
                <RichTextEditor
                  content={form.bioEn || ''}
                  onChange={(html) => setForm({ ...form, bioEn: html })}
                  placeholder="Personal bio..."
                  minHeight="150px"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                <input
                  type="url"
                  value={form.linkedin}
                  onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://linkedin.com/in/xxx"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">确认删除</h3>
            <p className="text-gray-600 mb-4">确定要删除这个团队成员吗？此操作无法撤销。</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
